import { useCallback } from 'react'
import { useFeedConfig } from '../../contexts/feedConfigContext'
import { CardHeader } from './CardHeader'
import { SharePostButton } from './PostEngagement'
import { usePostEngagement } from './postEngagementState'
import { getSelections, getSelectionSet, selectionPresentation } from '../../utils/selectionSet'
import { formatOdds, type OddsFormat } from '../../utils/odds'
import { useI18n } from '../../i18n'
import { useOddsFormat } from '../../i18n/hostFormat'
import { winningTicketHeaderText } from '../../branding/localizedCopy'
import { COMPACT_TICKET_LEG_LIMIT, ticketPaletteStyle, winningTicketFollowLabel, winningTicketPersonaAvatar, type WinningTicketFollowControls } from './winningTicket'
import type { FeedCreator, FeedItem, OddsMap, Selection } from '../../types/feedContract'
import type { FeedMeasurement } from '../../hooks/useFeedMeasurement'
import './WinningTicketCard.css'

export interface WinningTicketCardProps extends WinningTicketFollowControls {
  item: FeedItem
  index: number
  measurement?: FeedMeasurement
  /** Same contract as CardSelections: overrides the host's odds format. */
  oddsFormat?: OddsFormat
  /** `compact` fits the fixed-size carousel tile (CompactCard's box). */
  variant?: 'full' | 'compact'
  darkModeOverride?: boolean
  showPostEngagement?: boolean
}

function TrophyIcon() {
  return (
    <svg className="winning-ticket-icon winning-ticket-trophy" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
    </svg>
  )
}

/** Marks the persona as the feed's own account, as the RSI design shows it. */
function OfficialBadge() {
  const { t } = useI18n()
  return (
    <svg className="winning-ticket-official" width="16" height="16" viewBox="0 0 24 24" role="img" aria-label={t('card.officialAccount')}>
      <path fill="currentColor" d="m12 1.5 2.4 1.9 3-.3 1 2.9 2.7 1.4-.7 3 .7 3-2.7 1.4-1 2.9-3-.3L12 19.3l-2.4-1.9-3 .3-1-2.9L2.9 13.4l.7-3-.7-3L5.6 6l1-2.9 3 .3L12 1.5Z" />
      <path d="m8.3 10.6 2.6 2.6 4.8-5" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function WonCheckIcon() {
  return (
    <svg className="winning-ticket-check" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="11" fill="currentColor" />
      <path d="m7 12.5 3.2 3.2L17 9" stroke="var(--feed-component-winning-ticket-body-bg, #1B4FBF)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/**
 * A ticket's prices were locked when the card was generated, and denorm
 * derives their American strings from the locked decimals, so the payload pair
 * is consistent. A price the payload lacks renders as nothing, not "--".
 */
function lockedOdds(odds: OddsMap | string | number | null | undefined, format: OddsFormat): string | null {
  if (odds == null || odds === '') return null
  const formatted = formatOdds(odds, format)
  return formatted === '--' ? null : formatted
}

function TicketLeg({ selection, oddsFormat }: { selection: Selection; oddsFormat: OddsFormat }) {
  const presentation = selectionPresentation(selection)
  const odds = lockedOdds(selection.odds, oddsFormat)
  return (
    <li className="winning-ticket-leg leg-won">
      <WonCheckIcon />
      <span className="winning-ticket-leg-label">
        <span className="winning-ticket-leg-selection">{presentation.selection}</span>
        {presentation.market ? (
          <span className="winning-ticket-leg-market"> {presentation.market}</span>
        ) : null}
      </span>
      {odds ? <span className="winning-ticket-leg-odds">{odds}</span> : null}
    </li>
  )
}

export function WinningTicketCard({
  item,
  index,
  measurement,
  viewerIdentified,
  oddsFormat: oddsFormatOverride,
  variant = 'full',
  darkModeOverride,
  showPostEngagement = true,
  isFollowing,
  isFollowPending,
  onToggleFollow,
}: WinningTicketCardProps) {
  const { branding } = useFeedConfig()
  const i18n = useI18n()
  const { t, tCount } = i18n
  const hostOddsFormat = useOddsFormat()
  const oddsFormat = oddsFormatOverride ?? hostOddsFormat
  const tokens = branding.components.winningTicket
  const compact = variant === 'compact'
  const selectionSet = getSelectionSet(item)
  const selections = getSelections(selectionSet)
  const combined = lockedOdds(selectionSet.summary?.odds, oddsFormat)
  const persona: Partial<FeedCreator> = item.creator || {}
  const personaName = persona.display_name || persona.name || 'WinningTickets'
  const personaAvatar = winningTicketPersonaAvatar(item, tokens)
  const followTarget = item.cta?.kind === 'follow_creator' && item.cta.target ? item.cta.target : null
  const showFollow = Boolean(followTarget) && viewerIdentified && branding.presentation.showFollowButton
  const following = isFollowing(followTarget, false)
  const { recordShare } = usePostEngagement(item, measurement, index, compact ? 'compact' : 'full')
  const observeCard = useCallback((node: HTMLElement | null) => measurement?.observeRef(node), [measurement])
  const isDarkMode = darkModeOverride ?? branding.darkMode
  // The tile has a fixed height: past the limit, one leg fewer plus a count.
  const shownLegs = compact && selections.length > COMPACT_TICKET_LEG_LIMIT
    ? selections.slice(0, COMPACT_TICKET_LEG_LIMIT - 1)
    : selections
  const hiddenLegs = selections.length - shownLegs.length
  // A card colored for its team overrides the org tokens on the ticket block
  // alone; the persona header and the card chrome keep the feed's look.
  const paletteStyle = ticketPaletteStyle(item)

  // The perforation only precedes a pill or credit; an anonymous viewer of a
  // creator's ticket sees neither.
  const footer = showFollow && followTarget ? (
    <button
      type="button"
      className={`winning-ticket-follow${following ? ' is-following' : ''}`}
      disabled={isFollowPending(followTarget)}
      onClick={() => onToggleFollow(followTarget, following, {
        creator: { id: followTarget, display_name: item.cta?.target_name },
        postId: item.id,
      })}
    >
      {following ? t('card.following') : winningTicketFollowLabel(item.cta?.label, t('card.follow'))}
    </button>
  ) : !followTarget && tokens.feedCreditName ? (
    <p className="winning-ticket-credit">{tokens.feedCreditName}</p>
  ) : null

  const share = (
    <SharePostButton
      postId={item.id}
      selectionSet={selectionSet}
      enabled={showPostEngagement && item.share_enabled !== false}
      onShared={recordShare}
    />
  )

  return (
    <article
      ref={observeCard}
      className={compact ? 'compact-card winning-ticket-card winning-ticket-compact' : 'feed-card winning-ticket-card'}
      data-post-id={item.id}
      data-position={index}
      data-ranking-version={item.ranking_version || undefined}
      data-content-origin="winning_ticket"
      data-ticket-variant={variant}
      data-share-label-mode={branding.presentation.shareLabelMode}
      {...(isDarkMode ? { 'data-dark-mode': '' } : {})}
    >
      {compact ? (
        <div className="winning-ticket-compact-header">
          <img className="winning-ticket-compact-avatar" src={personaAvatar} alt="" />
          <span className="winning-ticket-compact-name">{personaName}</span>
          <OfficialBadge />
          <span className="winning-ticket-compact-share">{share}</span>
        </div>
      ) : (
        <CardHeader
          username={personaName}
          avatarUrl={personaAvatar}
          badge={<OfficialBadge />}
          canFollow={false}
          action={share}
          isSharedEntry={Boolean(item.is_shared_entry)}
        />
      )}
      <div className="winning-ticket" style={paletteStyle} {...(paletteStyle ? { 'data-team-palette': item.ticket_palette?.team_id || '' } : {})}>
        <div className="winning-ticket-band">
          {tokens.headerIconUrl ? <img className="winning-ticket-icon" src={tokens.headerIconUrl} alt="" /> : <TrophyIcon />}
          <span className="winning-ticket-band-text">{winningTicketHeaderText(branding, i18n)}</span>
          {combined ? <span className="winning-ticket-band-odds">{combined}</span> : null}
        </div>
        <div className="winning-ticket-body">
          <ol className="winning-ticket-legs">
            {shownLegs.map((selection) => (
              <TicketLeg key={selection.id} selection={selection} oddsFormat={oddsFormat} />
            ))}
          </ol>
          {hiddenLegs > 0 ? <p className="winning-ticket-more-legs">{tCount('card.moreLegs', hiddenLegs)}</p> : null}
          {footer ? (
            <>
              <div className="winning-ticket-divider" role="presentation" />
              {footer}
            </>
          ) : null}
        </div>
      </div>
    </article>
  )
}
